/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/recordingManagementApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    API calls for Recording Management module.
    Centralized API functions for better maintainability.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import { VoiceRecording, RecordingFilters, RecordingUploadData, RecordingTTSData } from '../types';

export interface FetchRecordingsParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  category?: string;
  language?: string;
  is_ai_generated?: boolean;
  status?: string;
  domainId?: number;
}

export interface FetchRecordingsResponse {
  success: boolean;
  data: VoiceRecording[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Fetch recordings with pagination and filters
 */
export async function fetchRecordings(
  params: FetchRecordingsParams,
): Promise<FetchRecordingsResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search && { search: params.search }),
    ...(params.category && { category: params.category }),
    ...(params.language && { language: params.language }),
    ...(params.is_ai_generated !== undefined && {
      is_ai_generated: params.is_ai_generated.toString(),
    }),
    ...(params.status && { status: params.status }),
    ...(params.domainId != null && { domainId: params.domainId.toString() }),
  });

  const response = await fetch(`${buildApiUrl('/api/telephony/recordings')}?${queryParams}`, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || `HTTP ${response.status}`);
  }

  const payload = await response.json();
  return {
    success: payload.success ?? true,
    data: Array.isArray(payload?.data) ? payload.data : [],
    pagination: payload?.pagination ?? {
      page: params.page,
      limit: params.limit,
      total: 0,
      totalPages: 0,
    },
  };
}

/**
 * Get recording by ID
 */
export async function getRecordingById(id: number | string): Promise<VoiceRecording> {
  const response = await fetch(buildApiUrl(`/api/telephony/recordings/${id}`), {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || `HTTP ${response.status}`);
  }

  const payload = await response.json();
  return payload.data;
}

/**
 * Create recording from file upload
 */
export async function createRecordingFromFile(data: RecordingUploadData): Promise<VoiceRecording> {
  const formData = new FormData();
  formData.append('name', data.name);
  formData.append('description', data.description || '');
  formData.append('category', data.category);
  formData.append('language', data.language);
  formData.append('file', data.file);

  const headers = getApiHeaders();
  delete (headers as any)['Content-Type']; // Let browser set multipart boundary

  const response = await fetch(buildApiUrl('/api/telephony/recordings'), {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to create recording');
  }

  const payload = await response.json();
  return payload.data;
}

/**
 * Create recording from TTS
 */
export async function createRecordingFromTts(data: RecordingTTSData): Promise<VoiceRecording> {
  const response = await fetch(buildApiUrl('/api/telephony/recordings'), {
    method: 'POST',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: data.name,
      description: data.description || '',
      category: data.category,
      language: data.language,
      text: data.text,
      ai_profile_id: data.ai_profile_id,
      start_silence_ms: data.start_silence_ms,
      end_silence_ms: data.end_silence_ms,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to create recording');
  }

  const payload = await response.json();
  return payload.data;
}

/**
 * Create recording from PBX Voice Guide
 */
export async function createRecordingFromSystem(data: any): Promise<VoiceRecording> {
  const response = await fetch(buildApiUrl('/api/telephony/recordings'), {
    method: 'POST',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: data.name,
      description: data.description || '',
      category: data.category,
      language: data.language,
      mode: 'system',
      voice_guide_id: data.voice_guide_id,
      voice_guide_file_path: data.voice_guide_file_path
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to create recording');
  }

  const payload = await response.json();
  return payload.data;
}

/**
 * Update recording
 */
export async function updateRecording(
  id: number | string,
  data: Partial<VoiceRecording> & { file?: File; text?: string; ai_profile_id?: number },
): Promise<VoiceRecording> {
  const url = buildApiUrl(`/api/telephony/recordings/${id}`);

  let response: Response;

  if (data.file) {
    // File upload mode
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      if (key !== 'file' && data[key as keyof typeof data] !== undefined) {
        formData.append(key, String(data[key as keyof typeof data]));
      }
    });
    formData.append('file', data.file);

    const headers = getApiHeaders();
    delete (headers as any)['Content-Type'];

    response = await fetch(url, {
      method: 'PUT',
      headers,
      body: formData,
    });
  } else {
    // JSON mode
    response = await fetch(url, {
      method: 'PUT',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to update recording');
  }

  const payload = await response.json();
  return payload.data;
}

/**
 * Delete recording
 */
export async function deleteRecording(id: number | string): Promise<void> {
  const response = await fetch(buildApiUrl(`/api/telephony/recordings/${id}`), {
    method: 'DELETE',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to delete recording');
  }
}

/**
 * Play recording (returns blob URL)
 */
export async function playRecording(id: number | string): Promise<Blob> {
  const response = await fetch(buildApiUrl(`/api/telephony/recordings/${id}/play`), {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to play recording');
  }

  return response.blob();
}

/**
 * Download recording
 */
export async function downloadRecording(id: number | string, filename: string): Promise<void> {
  const response = await fetch(buildApiUrl(`/api/telephony/recordings/${id}/download`), {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to download recording');
  }

  const blob = await response.blob();
  const downloadUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(downloadUrl);
}

/**
 * Generate TTS preview
 */
export async function generateTtsPreview(text: string, aiProfileId: number): Promise<Blob> {
  const response = await fetch(buildApiUrl('/api/telephony/recordings/generate-tts'), {
    method: 'POST',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      text,
      ai_profile_id: aiProfileId,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to generate TTS preview');
  }

  return response.blob();
}

/**
 * Fetch Voice Guide Stream as Blob
 */
export async function fetchVoiceGuideStreamBlob(path: string): Promise<Blob> {
  const response = await fetch(buildApiUrl(`/api/telephony/voice-guides/stream?path=${encodeURIComponent(path)}`), {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to fetch voice guide stream');
  }

  return response.blob();
}
